import { getAccessToken } from '../authentication/supabaseService';
import type { WasteActionGroupPayload, WasteActionPeriod } from '../../utils/analysis/wasteActionPayload';

// Types mirror z_ralph/tasks/1_new_tasks/analysis/api-contract.md (Money savers).

export type AnalysisSide = 'food' | 'drinks';
export type AnalysisUnit = 'kg' | 'l' | 'p';

export interface MoneySaverRestaurantBreakdown {
  restaurantId: string;
  restaurantName: string;
  qty: number | null;
  spend: number;
}

export interface MoneySaverMember {
  /** canonicalProductKey `${platform}::${identifier}` */
  key: string;
  name: string;
  /** platform (lowercase) or customPlatformName */
  supplier: string;
  side: AnalysisSide;
  unit: AnalysisUnit | null;
  /** latest €/unit (ex VAT), null = not comparable */
  unitPrice: number | null;
  priceDate: string | null;
  /** bought in scope in window, in `unit` */
  qty: number | null;
  /** € in scope in window */
  spend: number;
  /** max(0,(unitPrice - cheapest) * qty), 0 if not comparable */
  savings: number;
  /** Exact same-unit cheapest member used for positive savings; null otherwise. */
  swapTargetKey: string | null;
  isCheapest: boolean;
  comparable: boolean;
  /** restaurant name, set only on the cheapest member */
  cheapestBoughtBy: string | null;
  byRestaurant: MoneySaverRestaurantBreakdown[];
}

export interface MoneySaverGroup {
  id: string;
  name: string;
  category: string;
  side: AnalysisSide;
  /** unit of the comparable set with savings */
  unit: AnalysisUnit | null;
  savings: number;
  spend: number;
  qty: number | null;
  /** sorted: cheapest first, then by savings desc */
  members: MoneySaverMember[];
}

export interface MoneySaversResponse {
  /** YYYY-MM-DD */
  windowStart: string;
  windowEnd: string;
  totalSavings: number;
  /** only savings > 0, sorted desc */
  groups: MoneySaverGroup[];
  /** productKey -> groupId for ALL multi-member groups */
  groupByKey: Record<string, string>;
  groupNames: Record<string, { name: string; category: string }>;
}

/** GET /api/analysis/money-savers — superadmin identity only. `restaurantId` may be ALL_RESTAURANTS_ID. */
export async function fetchMoneySavers(restaurantId: string): Promise<MoneySaversResponse> {
  const token = await getAccessToken();
  const res = await fetch(`/api/analysis/money-savers?restaurantId=${encodeURIComponent(restaurantId)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Failed to fetch money savers: ${res.statusText}`);
  }
  return res.json();
}

export interface WasteActionSuggestionResult {
  groupId: string;
  status: 'ok' | 'failed';
  items: { text: string }[];
}

export async function suggestWasteActionItems(
  request: { restaurantId: string; groups: WasteActionGroupPayload[]; period: WasteActionPeriod; wastePercentage: number },
  signal?: AbortSignal,
): Promise<{ results: WasteActionSuggestionResult[] }> {
  const token = await getAccessToken();
  const res = await fetch('/api/analysis/waste-action-items/suggest', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
    signal,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Failed to suggest waste action items: ${res.statusText}`);
  }
  return res.json();
}
